<template>
  <page>
    <template #header-bar>
      <page-header-bar>
        <span class="ml-5">Reviews</span>
        <template #actions>
          <v-tooltip bottom :disabled="canCreateReviews">
            <template #activator="{ on }">
              <span v-on="on">
                <action-button
                  data-cy="add-review-button"
                  color="primary"
                  :disabled="!canCreateReviews"
                  @click="onAddReview"
                >
                  <v-icon left>add</v-icon>
                  Add Review
                </action-button>
              </span>
            </template>
            <span>{{ createReviewDisabledMessage }}</span>
          </v-tooltip>
        </template>
      </page-header-bar>
    </template>
    <template #content>
      <page-tabs
        v-model="activeTabIndex"
        class="reviews-page-tabs"
        :tabs="tabs"
        value-prop="name"
        count-prop="count"
        use-tab-routes
      >
        <page-tab-panel v-for="tab in tabs" :key="tab.value">
          <div class="d-flex justify-center fill-height pt-12 px-10">
            <div class="reviews d-flex flex-column">
              <v-alert v-if="summariesError" type="error" text dense>
                <div class="d-flex align-center">
                  <span>
                    Review progress counts could not be loaded.
                  </span>
                  <v-spacer />
                  <v-btn
                    text
                    small
                    color="error"
                    @click="
                      loadProgressSummariesForReviewsWithReferences
                    "
                  >
                    Retry
                  </v-btn>
                </div>
              </v-alert>
              <v-row
                v-if="$apollo.loading"
                no-gutters
                class="flex-column"
              >
                <v-skeleton-loader
                  v-for="i in 5"
                  :key="i"
                  class="mb-6"
                  type="article"
                />
              </v-row>
              <review-card-list
                v-else-if="tab.reviews.length > 0"
                class="flex-grow-1"
                :reviews="tab.reviews"
                @archive="onArchiveRequest"
                @unarchive="onUnarchiveRequest"
                @delete="onDeleteRequest"
              />
              <page-content-placeholder
                v-else-if="tab.value === 'ARCHIVED'"
                icon="assignment"
                prompt-text="Archive empty"
              >
                <template #message>
                  <div class="text-center message">
                    Archived reviews are stored here and can be
                    unarchived at anytime.
                  </div>
                </template>
                <template #action>
                  <action-button
                    color="primary"
                    @click="$router.push('/reviews')"
                  >
                    <v-icon left>arrow_back</v-icon>
                    <span>Go back</span>
                  </action-button>
                </template>
              </page-content-placeholder>
              <page-content-placeholder
                v-else
                icon="assignment"
                prompt-text="Add a Review"
              >
                <template #message>
                  <div class="text-center message">
                    Your review list is currently empty.<br />
                    Get started by adding a new review.
                  </div>
                </template>
                <template #action>
                  <v-tooltip bottom :disabled="canCreateReviews">
                    <template #activator="{ on }">
                      <span v-on="on">
                        <action-button
                          color="primary"
                          :disabled="!canCreateReviews"
                          @click="onAddReview"
                        >
                          <v-icon left>add</v-icon>
                          <span>Add Review</span>
                        </action-button>
                      </span>
                    </template>
                    <span>{{ createReviewDisabledMessage }}</span>
                  </v-tooltip>
                </template>
              </page-content-placeholder>
            </div>
          </div>
        </page-tab-panel>
      </page-tabs>
      <confirmation-dialog
        v-model="showDeleteConfirmation"
        :name="deleteCandidate.title || ''"
        @confirm="onDeleteConfirm"
      />
      <confirmation-dialog
        v-model="showArchiveConfirmation"
        action-title="Archive"
        action-color="primary"
        action-icon="inventory_2"
        :name="archiveCandidate.title || ''"
        @confirm="onArchiveConfirm"
      >
        Are you sure you want to archive this review? It will still be
        accessible via the Archived tab.
      </confirmation-dialog>
    </template>
  </page>
</template>

<script>
import Page from '@/components/Common/Page';
import PageHeaderBar from '@/components/Common/PageHeaderBar';
import PageTabs from '@/components/Common/PageTabs';
import PageTabPanel from '@/components/Common/PageTabPanel';
import ReviewCardList from '@/components/ReviewsPage/ReviewCardList';
import ConfirmationDialog from '@/components/ReviewsPage/ConfirmationDialog';
import PageContentPlaceholder from '@/components/Common/PageContentPlaceholder';
import ActionButton from '@/components/Common/ActionButton';
import {
  Review,
  Researcher,
  ScreeningProgressSummary,
  DataExtractionProgressSummary,
} from '@/models';
import {
  GetReviews,
  GetReviewProgressSummaries,
  GetCurrentResearcher,
} from '@/graphql';
import { mapActions } from 'vuex';
import { startCase } from 'lodash';

const ReviewsPageTabs = Object.freeze({
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
});

const TAB_TO_PROPS = {
  [ReviewsPageTabs.ACTIVE]: {
    reviews: (pageData) => pageData?.reviews || [],
    count: (pageData) => pageData?.reviews?.length || 0,
  },
  [ReviewsPageTabs.ARCHIVED]: {
    reviews: (pageData) => pageData?.archivedReviews || [],
    count: (pageData) => pageData?.archivedReviews?.length || 0,
  },
};

export default {
  name: 'ReviewsPage',
  components: {
    Page,
    PageHeaderBar,
    PageTabs,
    PageTabPanel,
    ReviewCardList,
    ActionButton,
    ConfirmationDialog,
    PageContentPlaceholder,
  },
  data: () => ({
    activeTabIndex: 0,
    pageData: [],
    currentResearcher: null,
    showDeleteConfirmation: false,
    deleteCandidate: {},
    showArchiveConfirmation: false,
    archiveCandidate: {},
    summariesError: false,
  }),
  computed: {
    hasAnyReviews() {
      return (
        this.activeReviews.length > 0 ||
        this.archivedReviews.length > 0
      );
    },
    selectedHasReviews() {
      return this.tabs[this.activeTabIndex].reviews.length > 0;
    },
    activeReviews() {
      return this.pageData?.reviews || [];
    },
    archivedReviews() {
      return this.pageData?.archivedReviews || [];
    },
    tabs() {
      return Object.values(ReviewsPageTabs).map((tab) => ({
        value: tab,
        name: startCase(tab),
        reviews: TAB_TO_PROPS[tab].reviews(this.pageData),
        count: TAB_TO_PROPS[tab].count(this.pageData),
      }));
    },
    activeTab() {
      return this.tabs[this.activeTabIndex].value;
    },
    isArchivedTab() {
      return this.activeTab === ReviewsPageTabs.ARCHIVED;
    },
    canCreateReviews() {
      return !!(
        this.currentResearcher &&
        (this.currentResearcher.isSuperAdmin ||
          this.currentResearcher.canCreateReviews)
      );
    },
    createReviewDisabledMessage() {
      return 'External accounts can only access reviews they have been added to. Ask a lead reviewer to invite you, or contact an administrator.';
    },
  },
  apollo: {
    pageData: {
      query: GetReviews,
      update: (data) => ({
        reviews: data.reviews.map((review) => new Review(review)),
        archivedReviews: data.archivedReviews.map(
          (review) => new Review(review)
        ),
      }),
      result() {
        this.loadProgressSummariesForReviewsWithReferences();
      },
    },
    currentResearcher: {
      query: GetCurrentResearcher,
      update: (data) => new Researcher(data.currentResearcher),
    },
  },
  mounted() {
    // Clear any in-progress upload state.
    this.clearUploadModalState();
    this.clearUploadProgressToastState();
  },
  methods: {
    ...mapActions({
      saveReview: 'review/saveReview',
      archiveReview: 'review/archiveReview',
      unarchiveReview: 'review/unarchiveReview',
      deleteReview: 'review/deleteReview',
      clearUploadModalState: 'uploadReferences/clearModalState',
      clearUploadProgressToastState:
        'uploadReferences/clearProgressToastState',
    }),
    async loadProgressSummariesForReviewsWithReferences() {
      // The apollo result() hook also fires on loading/error frames, before
      // update() has replaced the initial pageData value with the real object.
      const { reviews, archivedReviews } = this.pageData ?? {};
      if (
        !Array.isArray(reviews) ||
        !Array.isArray(archivedReviews)
      ) {
        return;
      }

      const allReviews = [...reviews, ...archivedReviews];
      const reviewsWithReferences = allReviews.filter(
        (review) =>
          review.uniqueReferencesCount &&
          review.uniqueReferencesCount > 0
      );

      if (reviewsWithReferences.length === 0) return;

      this.summariesError = false;
      try {
        const { data } = await this.$apollo.query({
          query: GetReviewProgressSummaries,
          variables: {
            ids: reviewsWithReferences.map((review) => review.id),
          },
        });

        // Update reviews with progress summaries
        data.reviewsByIds.forEach((reviewWithProgress) => {
          const review = allReviews.find(
            (r) => r.id === reviewWithProgress.id
          );
          if (review) {
            // Instantiate progress summaries as proper model objects
            if (reviewWithProgress.screeningProgressSummary) {
              review.screeningProgressSummary = new ScreeningProgressSummary(
                reviewWithProgress.screeningProgressSummary
              );
            }
            if (
              reviewWithProgress.fullTextReviewScreeningProgressSummary
            ) {
              review.fullTextReviewScreeningProgressSummary = new ScreeningProgressSummary(
                reviewWithProgress.fullTextReviewScreeningProgressSummary
              );
            }
            if (reviewWithProgress.dataExtractionProgressSummary) {
              review.dataExtractionProgressSummary = new DataExtractionProgressSummary(
                reviewWithProgress.dataExtractionProgressSummary
              );
            }
          }
        });
      } catch {
        this.summariesError = true;
      }
    },
    async onAddReview() {
      if (!this.canCreateReviews) return;
      const review = new Review({ title: 'New Review' });
      const newReview = await this.saveReview({ review });
      this.$router.push({
        path: `/review/${newReview.id}/settings`,
        query: { new: true },
      });
    },
    async onDeleteRequest(review) {
      this.showDeleteConfirmation = true;
      this.deleteCandidate = review;
    },
    async onArchiveRequest(review) {
      this.showArchiveConfirmation = true;
      this.archiveCandidate = review;
    },
    async onUnarchiveRequest(review) {
      await this.unarchiveReview(review);
    },
    async onDeleteConfirm() {
      await this.deleteReview(this.deleteCandidate);
      if (this.isArchivedTab && !this.hasAnyReviews) {
        this.$router.push({ path: '/reviews' });
      }
    },
    async onArchiveConfirm() {
      await this.archiveReview(this.archiveCandidate);
    },
  },
};
</script>

<style>
.reviews-page-tabs {
  height: 100%;
}
.reviews-page-tabs > div:nth-child(2) {
  height: calc(100% - 48px * 2);
}
.reviews-page-tabs .v-tabs-items {
  height: 100%;
}
.reviews-page-tabs .v-window__container {
  height: 100%;
}
.reviews-page-tabs .v-window-item {
  height: 100%;
}
</style>

<style scoped>
.reviews {
  max-width: 1400px;
  width: 100%;
}
</style>
